import React, {useEffect, useState} from 'react';
import axios from 'axios';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import {toast} from 'react-toastify';
import Draggable from 'react-draggable';
import {Box, CircularProgress, Divider, IconButton, Toolbar} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

function PaperComponent(props) {
    return (
        <Draggable
            handle="#data-load-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} />
        </Draggable>
    );
}

export default function DataLoadDialog({visible, onClose}) {
    const [modules, setModules] = useState([]);
    const [filteredModules, setFilteredModules] = useState([]);
    const [search, setSearch] = useState('');
    const [loadingModuleIds, setLoadingModuleIds] = useState([]);
    const [isSending, setIsSending] = useState(false); // New state

    const loadModules = async () => {
        const response = await axios.get('/dataload');
        setModules(response.data);
        filterModules(response.data, search);
    };

    useEffect(() => {
        if (visible) {
            setSearch('');
            loadModules();
        }
    }, [visible]);

    useEffect(() => {
        filterModules(modules, search);
    }, [search, modules]);

    const filterModules = (modules, search) => {
        if (search.length === 0) {
            setFilteredModules(modules);
        } else {
            const filtered = modules.filter(m =>
                m.identifier.toLowerCase().includes(search.toLowerCase())
            );
            setFilteredModules(filtered);
        }
    };

    const handleClick = async (module) => {
        if (loadingModuleIds.includes(module.identifier)) return;

        setLoadingModuleIds(prev => [...prev, module.identifier]);

        try {
            const qs = require('qs');
            if (!module.selected) {
                await axios.post('/dataload/selected', qs.stringify({'moduleIdentifier': module.identifier}));
            } else {
                await axios.delete(`/dataload/selected/${module.identifier}`);
            }
            await loadModules();
        } catch (error) {
            console.error('Error updating module:', error);
            toast.error('Error updating module');
        } finally {
            setLoadingModuleIds(prev => prev.filter(id => id !== module.identifier));
        }
    };

    const onSend = async () => {
        setIsSending(true);
        try {
            await axios.post('/dataload/send');
            toast.success('Data load sent');
            onClose();
        } catch (error) {
            toast.error('Failed to send data load');
        } finally {
            setIsSending(false);
        }
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
    };

    return (
        <Dialog
            open={visible}
            onClose={onClose}
            fullWidth
            PaperComponent={PaperComponent}
            BackdropProps={{
                sx: {
                    backgroundColor: "rgba(0, 0, 0, 0.2)",
                },
            }}
        >
            <Toolbar disableGutters variant='dense' sx={{m: 0, p: 0, display: 'flex'}}>
                <DialogTitle id="data-load-dialog-title" sx={{cursor: 'move', flexGrow: 1}}>Data Load</DialogTitle>
                <IconButton
                    onClick={onClose}
                    disabled={isSending}
                    sx={{borderRadius: 0}}
                >
                    <CloseIcon/>
                </IconButton>
            </Toolbar>
            <DialogContent sx={{m: 0, p: 0, paddingLeft: '20px', paddingRight: '20px'}}>
                <TextField
                    label="Search"
                    variant="standard"
                    value={search}
                    fullWidth
                    onChange={handleSearchChange}
                    disabled={isSending}
                />
                <TableContainer sx={{
                    minHeight: '400px',
                    maxHeight: '400px',
                    overflowY: 'auto',
                }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell align="right">Selected</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredModules.map((module) => {
                                const isLoading = loadingModuleIds.includes(module.identifier);
                                return (
                                    <TableRow
                                        hover
                                        key={module.identifier}
                                        onClick={() => !isLoading && !isSending && handleClick(module)}
                                        sx={{
                                            cursor: 'pointer',
                                            opacity: isLoading ? 0.5 : 1,
                                        }}
                                    >
                                        <TableCell>{module.name}</TableCell>
                                        <TableCell align="right">
                                            {isLoading ? (
                                                <Box sx={{
                                                    height: 43,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'right'
                                                }}>
                                                    <CircularProgress size={20}/>
                                                </Box>
                                            ) : (
                                                <Checkbox checked={module.selected}/>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button fullWidth onClick={onSend} disabled={isSending}>
                    {isSending ? <CircularProgress size={24}/> : 'Send'}
                </Button>
                <Divider orientation="vertical" flexItem/>
                <Button fullWidth onClick={onClose} disabled={isSending}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
